/**
 * Alpine glue for an embedded, scriptable review player (Gumlet via PlayerJS,
 * YouTube via the IFrame API, Google Drive as a non-scriptable preview).
 *
 * The actual player objects live in a module-level Map (`instances`), NOT in
 * Alpine reactive state — neither the YT/PlayerJS objects nor anything closing
 * over $wire is ever made reactive or serialized (CLAUDE.md proxy rule). The
 * Alpine component itself holds only primitives for the view (ready / errored).
 *
 * Gumlet's PlayerJS is bundled (small, deterministic). The YouTube IFrame API
 * must be loaded from Google's domain, so it's injected lazily once.
 */
import { Player as GumletPlayer } from '@gumlet/player.js';
import { registerPlayer, unregisterPlayer, setActivePlayer } from './video-players';

let ytApiPromise = null;

/** Load the YouTube IFrame API once; it self-injects a single global. */
function loadYouTubeApi() {
    if (window.YT && window.YT.Player) {
        return Promise.resolve(window.YT);
    }

    if (ytApiPromise) {
        return ytApiPromise;
    }

    ytApiPromise = new Promise((resolve) => {
        const previous = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
            if (typeof previous === 'function') previous();
            resolve(window.YT);
        };

        if (!document.querySelector('script[data-youtube-iframe-api]')) {
            const tag = document.createElement('script');
            tag.src = 'https://www.youtube.com/iframe_api';
            tag.setAttribute('data-youtube-iframe-api', '');
            document.head.appendChild(tag);
        }
    });

    return ytApiPromise;
}

// Non-reactive per-field player instances. Keyed by stable field_id.
const instances = new Map();

// Returns a Promise<number|null> — null for Google Drive (not readable).
function readCurrentTime(inst) {
    if (inst.provider === 'youtube') {
        if (inst.player && inst.ready && typeof inst.player.getCurrentTime === 'function') {
            try {
                return Promise.resolve(Math.floor(inst.player.getCurrentTime() || 0));
            } catch (e) {
                console.warn('[video] youtube getCurrentTime threw', e);
            }
        }
        return Promise.resolve(inst.lastTime || 0);
    }

    if (inst.provider === 'gumlet') {
        if (!inst.player) {
            console.warn('[video] gumlet getCurrentTime: no player yet');
            return Promise.resolve(inst.lastTime || 0);
        }

        // v3 PlayerJS methods return promises. Query the embed on demand, with a
        // timeout fallback to the cached timeupdate value if it never answers.
        return new Promise((resolve) => {
            let settled = false;
            const finish = (value, source) => {
                if (settled) return;
                settled = true;
                const secs = Math.max(0, Math.floor(Number(value) || 0));
                console.log(`[video] gumlet getCurrentTime → ${secs}s (via ${source}) [ready=${inst.ready}, cachedLastTime=${inst.lastTime}]`);
                resolve(secs);
            };

            Promise.resolve()
                .then(() => inst.player.getCurrentTime())
                .then((t) => finish(t, 'playerjs promise'))
                .catch((e) => {
                    console.warn('[video] gumlet getCurrentTime rejected', e);
                    finish(inst.lastTime, 'lastTime after reject');
                });

            // Fallback if the embed never answers (e.g. PlayerJS not connected).
            setTimeout(() => finish(inst.lastTime, 'timeout fallback → lastTime'), 800);
        });
    }

    return Promise.resolve(null); // Google Drive
}

function seekInstance(inst, seconds) {
    seconds = Math.max(0, Number(seconds) || 0);

    if (inst.provider === 'youtube') {
        // Queue if the player exists but isn't ready yet (e.g. email auto-seek).
        if (inst.player && inst.ready) {
            try {
                inst.player.seekTo(seconds, true);
                if (typeof inst.player.playVideo === 'function') inst.player.playVideo();
            } catch (e) { /* ignore */ }
        } else {
            inst.pendingSeek = seconds;
        }
        return;
    }

    if (inst.provider === 'gumlet') {
        if (inst.player && inst.ready) {
            inst.player.setCurrentTime(seconds);
            if (typeof inst.player.play === 'function') inst.player.play();
        } else {
            inst.pendingSeek = seconds;
        }
        return;
    }

    // Google Drive (or no player): open the provider viewer at the timestamp.
    if (inst.watchUrl) {
        window.open(`${inst.watchUrl}?t=${seconds}`, '_blank', 'noopener');
    }
}

function playInstance(inst) {
    if (!inst.player) return;
    try {
        if (inst.provider === 'youtube') inst.player.playVideo();
        else if (inst.provider === 'gumlet') inst.player.play();
    } catch (e) { /* ignore */ }
}

function pauseInstance(inst) {
    if (!inst.player) return;
    try {
        if (inst.provider === 'youtube') inst.player.pauseVideo();
        else if (inst.provider === 'gumlet') inst.player.pause();
    } catch (e) { /* ignore */ }
}

function togglePlayInstance(inst) {
    if (inst.playing) pauseInstance(inst);
    else playInstance(inst);
}

export default function videoReviewPlayer(config = {}) {
    return {
        ready: false,
        errored: false,
        _fieldId: null,

        init() {
            const fieldId = String(config.fieldId);
            this._fieldId = fieldId;

            // Idempotent re-init: drop any stale instance/handle first.
            this.teardown(fieldId);

            const inst = {
                fieldId,
                provider: config.provider,
                videoRef: config.id,
                label: config.label || 'Video',
                watchUrl: config.watchUrl,
                canCapture: !!config.canCaptureInPage,
                canSeek: !!config.canSeekInPage,
                player: null,
                ready: false,
                playing: false,
                pendingSeek: null,
                lastTime: config.startSeconds || 0,
            };
            instances.set(fieldId, inst);

            const onError = () => { this.errored = true; };

            if (config.provider === 'youtube') {
                loadYouTubeApi().then((YT) => {
                    inst.player = new YT.Player(this.$refs.mount, {
                        host: 'https://www.youtube-nocookie.com',
                        videoId: config.id,
                        playerVars: { rel: 0, modestbranding: 1, start: config.startSeconds || 0 },
                        events: {
                            onReady: () => {
                                inst.ready = true;
                                this.ready = true;
                                if (inst.pendingSeek !== null) { seekInstance(inst, inst.pendingSeek); inst.pendingSeek = null; }
                            },
                            onStateChange: (event) => {
                                try { inst.lastTime = Math.floor(inst.player.getCurrentTime() || 0); } catch (e) { /* ignore */ }
                                // YT.PlayerState: 1 = playing, 2 = paused, 0 = ended.
                                if (event && event.data === 1) { inst.playing = true; setActivePlayer(inst.fieldId); }
                                else if (event && (event.data === 2 || event.data === 0)) { inst.playing = false; }
                            },
                            onError,
                        },
                    });
                }).catch(onError);
            } else if (config.provider === 'gumlet') {
                try {
                    // Build the iframe in JS and attach PlayerJS immediately, so the
                    // Player is listening before the iframe fires its load/ready
                    // handshake (PlayerJS can't catch a ready that fired pre-attach).
                    const iframe = document.createElement('iframe');
                    iframe.src = config.embedUrl;
                    iframe.className = 'absolute inset-0 h-full w-full';
                    iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
                    iframe.setAttribute('allowfullscreen', '');
                    iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
                    iframe.title = config.label || 'Video';
                    this.$refs.mount.appendChild(iframe);

                    console.log('[video] gumlet: built iframe + attaching PlayerJS for', config.id);
                    inst.player = new GumletPlayer(iframe);
                    inst.player.on('ready', () => {
                        console.log('[video] gumlet: READY fired for', config.id);
                        inst.ready = true;
                        this.ready = true;
                        if (inst.pendingSeek !== null) {
                            seekInstance(inst, inst.pendingSeek);
                            inst.pendingSeek = null;
                        } else if (config.startSeconds) {
                            inst.player.setCurrentTime(config.startSeconds);
                        }
                    });
                    inst.player.on('timeupdate', (d) => {
                        inst.lastTime = Math.floor((d && d.seconds) || 0);
                        if (!inst._loggedTimeupdate) {
                            inst._loggedTimeupdate = true;
                            console.log('[video] gumlet: first TIMEUPDATE', d);
                        }
                    });
                    inst.player.on('play', () => { inst.playing = true; setActivePlayer(inst.fieldId); });
                    inst.player.on('pause', () => { inst.playing = false; });
                    inst.player.on('error', (e) => console.warn('[video] gumlet: error event', e));
                } catch (e) {
                    console.warn('[video] gumlet: PlayerJS setup threw', e);
                    onError();
                }
            }
            // Google Drive: no scriptable player; still registered below so the
            // composer can offer manual-timestamp capture against it.

            registerPlayer(fieldId, {
                fieldId,
                provider: inst.provider,
                videoRef: inst.videoRef,
                label: inst.label,
                canCapture: inst.canCapture,
                canSeek: inst.canSeek,
                watchUrl: inst.watchUrl,
                getCurrentTime: () => readCurrentTime(inst),
                seek: (seconds) => seekInstance(inst, seconds),
                play: () => playInstance(inst),
                pause: () => pauseInstance(inst),
                togglePlay: () => togglePlayInstance(inst),
            });
        },

        teardown(fieldId) {
            unregisterPlayer(fieldId);
            const inst = instances.get(fieldId);
            if (inst && inst.player && inst.provider === 'youtube' && typeof inst.player.destroy === 'function') {
                try { inst.player.destroy(); } catch (e) { /* ignore */ }
            }
            instances.delete(fieldId);
        },

        destroy() {
            if (this._fieldId) this.teardown(this._fieldId);
        },
    };
}
